/**
 * Carrega os pedidos do PERÍODO escolhido (filtro no banco) e entrega à tela o dataset e a barra de
 * período já montada. A barra continua na tela enquanto os pedidos carregam, então trocar de período
 * nunca deixa a página em branco.
 */
import { createElement as h, type ReactElement } from "react";
import { StatusPanel, errorDetail } from "./StatusPanel.js";
import { PeriodToolbar, usePeriodSelection } from "./PeriodToolbar.js";
import { useOrdersDataset } from "../queries.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { PeriodKey, ResolvedPeriod } from "../period.js";

export interface PeriodOrdersContext {
  toolbar: ReactElement;
  period: ResolvedPeriod;
}

export interface PeriodOrdersBoundaryProps {
  /** Período inicial da tela quando a URL não traz um. */
  defaultPeriod: PeriodKey;
  children: (dataset: OrdersDataset, context: PeriodOrdersContext) => ReactElement;
}

export function PeriodOrdersBoundary({ defaultPeriod, children }: PeriodOrdersBoundaryProps): ReactElement {
  const { selection, period, setSelection } = usePeriodSelection(defaultPeriod);
  const query = useOrdersDataset({ from: period.from, to: period.to });
  const toolbar = h(PeriodToolbar, { selection, period, onChange: setSelection });
  if (query.isPending) {
    return h("div", { className: "flex flex-col gap-4" }, toolbar, h(StatusPanel, { kind: "loading", title: "Carregando pedidos do período…" }));
  }
  if (query.error) {
    return h("div", { className: "flex flex-col gap-4" }, toolbar, h(StatusPanel, { kind: "error", title: "Não foi possível carregar os pedidos", detail: errorDetail(query.error) }));
  }
  if (!query.data) {
    return h("div", { className: "flex flex-col gap-4" }, toolbar, h(StatusPanel, { kind: "empty", title: "Nenhum dado de pedidos retornado pela API da Plataforma" }));
  }
  return children(query.data, { toolbar, period });
}
